import { JSXElement, Match, mergeProps, Show, Switch } from 'solid-js';
import { Localized } from '~/i18n';
import { Button } from '~/components/ui/Button';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-solid';
import { PaginationTypes, usePaginationContext } from './PaginationContext.tsx';
import { twMerge } from 'tailwind-merge';

type PaginationActionsProps = {
  class?: string;
};

export default function PaginationActions(
  props: PaginationActionsProps
): JSXElement {
  const merged = mergeProps(
    {
      class: ''
    },
    props
  );
  const paginationContext = usePaginationContext();

  const getStartIndex = () => {
    return (
      paginationContext.pagination().pageIndex * paginationContext.pageSize + 1
    );
  };
  const getEndIndex = () => {
    return (
      paginationContext.pagination().pageIndex * paginationContext.pageSize +
      Math.min(
        paginationContext.pageSize,
        paginationContext.dataParser(paginationContext.queryObject.data).length
      )
    );
  };

  return (
    <Switch>
      <Match
        when={
          paginationContext.paginationType === PaginationTypes.PAGE_NAVIGATION
        }
      >
        <div
          class={twMerge(
            `flex justify-between items-center my-4`,
            merged.class
          )}
        >
          <div class={`text-sm`}>
            <Show when={!paginationContext.queryObject.isFetching}>
              <Localized
                translationKey={`commons.pagination.display_count`}
                vars={{
                  startIndex: getStartIndex(),
                  endIndex: getEndIndex(),
                  totalCount: paginationContext.pageDetails.totalSize
                }}
              />
            </Show>
          </div>
          <div class={`flex gap-2`}>
            <Button
              variant={`ghost`}
              size={`xs`}
              onClick={() => paginationContext.toPrevPage()}
              disabled={
                paginationContext.queryObject.isFetching ||
                !paginationContext.pageDetails.prevPageToken
              }
              data-testid={'table-pagination-prev-page'}
            >
              <ChevronLeftIcon class={`mr-1`} />
              <Localized translationKey={`commons.pagination.prev_action`} />
            </Button>
            <Button
              variant={`ghost`}
              size={`xs`}
              onClick={() => paginationContext.toNextPage()}
              disabled={
                paginationContext.queryObject.isFetching ||
                !paginationContext.pageDetails.nextPageToken
              }
              data-testid={'table-pagination-next-page'}
            >
              <Localized translationKey={`commons.pagination.next_action`} />
              <ChevronRightIcon class={`ml-1`} />
            </Button>
          </div>
        </div>
      </Match>
      <Match
        when={paginationContext.paginationType === PaginationTypes.LOAD_MORE}
      >
        <Show when={paginationContext.pageDetails.nextPageToken}>
          <div class={`flex justify-center mt-4`}>
            <Button
              variant={`outline`}
              size={`xs`}
              onClick={() => paginationContext.toNextPage()}
              loading={paginationContext.queryObject.isFetching}
              data-testid={`table-pagination-load-more-button`}
            >
              <Localized translationKey={`commons.pagination.load_more`} />
            </Button>
          </div>
        </Show>
      </Match>
    </Switch>
  );
}
